import { FunctionComponent, useState } from "react";
import {
	Alert,
	AlertColor,
	Box,
	CircularProgress,
	IconButton,
	LinearProgress,
	Typography,
} from "@mui/material";
import { useDocuments } from "../../context/DocumentContext";
import { colorGrayLight } from "../../styles/theme";
import { Delete } from "@mui/icons-material";
import NewsletterForm from "./NewsletterForm";
import { sendNewsletter } from "../../services/post";
import { NewsletterRequest } from "../../types";
import { useConfig } from "../../context/ConfigContext";
import { useClientAccessContext } from "../../context/ClientAccessContext";
import CustomAlert from "../CustomAlert";
import { EventName, useEventTracking } from "../../context/EventTrackingContext";

const Basket: FunctionComponent = () => {
	const { documentsInBasket, removeDocumentFromBasket, setDocumentsInBasket } =
		useDocuments();
	const { selectedTopic } = useConfig();
	const { selectedClient } = useClientAccessContext();
	const { trackEvent } = useEventTracking();
	const [loading, setLoading] = useState(false);
	const [alertConfig, setAlertConfig] = useState<{
		severity: AlertColor;
		message: string;
	} | null>(null);

	const handleSendNewsletter = async (
		email_subject: string,
		newsletter_introduction: string,
		recipients_emails: string[]
	) => {
		setLoading(true);
		const newsletterRequest: NewsletterRequest = {
			email_subject,
			newsletter_introduction,
			recipients_emails,
			document_ids: documentsInBasket.map((d) => d.id),
			client_name: selectedClient.clientName,
			topic_name: selectedTopic.name,
		};

		try {
			await sendNewsletter(newsletterRequest);
			trackEvent(EventName.NEWSLETTER_SENT, {
				properties: {
					clientId: selectedClient.clientId,
					clientName: selectedClient.clientName,
					topicId: selectedTopic.id,
					topicName: selectedTopic.name,
					documentIds: newsletterRequest.document_ids,
					documentCount: newsletterRequest.document_ids.length,
					recipientCount: recipients_emails.length,
				},
			});
			setDocumentsInBasket([]);
			setAlertConfig({
				severity: "success",
				message: "Newsletter sent successfully",
			});
		} catch (err: any) {
			console.error("Error while sending the newsletter", err);
			setAlertConfig({
				severity: "error",
				message: "Could not send the newsletter",
			});
		}
		setLoading(false);
	};

	return (
		<Box
			sx={{
				padding: "20px",
				borderRadius: "12px",
				bgcolor: "white",
				width: "1100px",
				height: "600px",
				display: "flex",
				gap: "40px",
				justifyContent: "space-between",
				position: "relative",
			}}
		>
			{loading && (
				<LinearProgress
					sx={{ position: "absolute", top: 0, left: 20, right: 20 }}
				/>
			)}

			{alertConfig && !loading && (
				<CustomAlert
					severity={alertConfig.severity}
					message={alertConfig.message}
				/>
			)}
			<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
				<Typography sx={{ marginBottom: "12px" }} variant="h3">
					Documents
				</Typography>

				<Box
					sx={{
						flex: 1,
						overflowY: "auto",
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						pr: 1,
					}}
				>
					{documentsInBasket.map((document) => (
						<Box sx={{ display: "flex", gap: "5px", alignItems: "center" }}>
							<IconButton onClick={() => removeDocumentFromBasket(document.id)}>
								<Delete />
							</IconButton>
							<Box
								sx={{
									backgroundColor: colorGrayLight,
									padding: "14px",
									borderRadius: "20px",
								}}
							>
								{document.title}
							</Box>
						</Box>
					))}
				</Box>
			</Box>
			<Box sx={{ display: "flex", flexDirection: "column" }}>
				<Typography sx={{ marginBottom: "12px" }} variant="h3">
					Newsletter Details
				</Typography>
				<NewsletterForm onSubmit={handleSendNewsletter} />
			</Box>
		</Box>
	);
};

export default Basket;
